import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Github, KeyRound, RefreshCw } from "lucide-react";
import { keys, signInUrl, useSignInProviders } from "@/api/hooks";
import { Mark } from "@/components/Mark";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

/** Error codes the API puts on `/login?error=` after a failed provider round trip. */
const ERRORS: Record<string, string> = {
  not_allowed: "That account has no Minne account yet. Ask an admin for an invite link.",
  account_disabled: "This account has been disabled by an admin.",
  invite_invalid: "This invite link is not valid.",
  invite_used: "This invite has already been used.",
  invite_expired: "This invite has expired.",
  invite_revoked: "This invite was withdrawn.",
  invite_email_mismatch: "This invite is for a different email address. Sign in with the account that uses it, or ask for a new invite.",
  access_denied: "You cancelled the sign-in at the provider.",
  invalid_state: "The sign-in took too long or the browser lost its cookie. Try again.",
  provider_error: "The provider did not complete the sign-in. Try again.",
  provider_unavailable: "The sign-in provider could not be reached.",
  unknown_provider: "That sign-in method is not enabled on this server.",
  sign_in_first: "Sign in before connecting another account.",
};

export function describeAuthError(code: string | null): string | null {
  if (!code) return null;
  return ERRORS[code] ?? `Sign-in failed (${code}).`;
}

export default function SignInPage({ returnTo: returnToProp }: { returnTo?: string }) {
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const providers = useSignInProviders();
  const isMock = import.meta.env.VITE_MOCK === "1";
  // The API's redirects pass returnTo in the query; in-app guards pass it as a prop.
  const returnTo = returnToProp ?? params.get("returnTo") ?? undefined;
  const error = describeAuthError(params.get("error"));
  const list = providers.data?.providers ?? [];
  const devOnly = providers.data?.dev && list.length === 0;

  return (
    <div className="flex min-h-app items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardContent className="py-8">
          <div className="flex items-center gap-2">
            <Mark className="size-10 shrink-0" />
            <span className="text-lg font-semibold tracking-tight">Minne</span>
          </div>
          <h1 className="mt-5 text-xl font-semibold tracking-tight">Sign in</h1>

          {error && (
            <p role="alert" className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}

          {providers.isLoading && <p className="mt-2 text-sm text-muted-foreground">Checking sign-in options…</p>}
          {providers.isError && (
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Could not reach the API. In development make sure it is running on port 4301.
            </p>
          )}

          {list.length > 0 && (
            <div className="mt-6 flex flex-col gap-2">
              {list.map((p) => (
                <Button key={p.id} asChild size="lg" variant={p.id === "github" ? "default" : "outline"}>
                  <a href={signInUrl(p.id, { returnTo })}>
                    {p.id === "github" ? <Github /> : <KeyRound />} Sign in with {p.name}
                  </a>
                </Button>
              ))}
            </div>
          )}

          {devOnly && (
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              This server runs with <code className="font-mono text-xs">AUTH_PROVIDERS=dev</code>: the API signs you in as a fixed user on the first request. If you see this page, the session cookie was not set — try again.
            </p>
          )}
          {providers.data && list.length === 0 && !providers.data.dev && (
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              No sign-in method is enabled. Set <code className="font-mono text-xs">AUTH_PROVIDERS</code> to <code className="font-mono text-xs">github</code> and/or <code className="font-mono text-xs">oidc</code> on the server.
            </p>
          )}

          {(devOnly || providers.isError) && (
            <div className="mt-4">
              <Button variant="outline" onClick={() => qc.invalidateQueries({ queryKey: keys.me })}>
                <RefreshCw /> Try again
              </Button>
            </div>
          )}

          {returnTo && returnTo !== "/" && <p className="mt-4 text-xs text-muted-foreground">You will be returned to {returnTo}.</p>}
          {isMock && <p className="mt-4 text-xs text-muted-foreground">Mock mode is on; this page should not appear.</p>}
        </CardContent>
      </Card>
    </div>
  );
}
